import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — французский текст
const lang = 'fr';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Un événement se crée en une seule fois avec le formulaire « Créer un événement ». Choisissez d'abord le type
        d'événement et la façon dont les courses y entrent : c'est ce qui détermine les champs affichés. Vous trouverez
        ci-dessous chaque étape et chaque variante avec des captures d'écran.
      </p>

      <Toc
        title="Sommaire"
        items={[
          ['access', 'Avant de commencer'],
          ['choose', 'Choisir la variante de l\'événement'],
          ['basics', 'Étape 1. L\'essentiel : nom, description, coorganisateurs'],
          ['type', 'Étape 2. Type d\'événement'],
          ['status', 'Étape 3. Statut et image'],
          ['registration', 'Étape 4. Inscriptions et visibilité'],
          ['scoring', 'Étape 5. Classements et points'],
          ['wildcards', 'Étape 6. Wild cards (course et tournoi)'],
          ['sync', 'Variante A. Courses trouvées par modèle de recherche'],
          ['manual', 'Variante B. Courses d\'autres utilisateurs ROUVY'],
          ['open', 'Variante C. Tournoi ouvert'],
          ['official', 'Variante D. Événement officiel'],
          ['after', 'Après la création : modification et coorganisateurs'],
          ['problems', 'Erreurs fréquentes']
        ]}
      />

      <section id="access">
        <h2>Avant de commencer</h2>
        <ul>
          <li>
            Le bouton <strong>« Créer un événement »</strong> <Num>1</Num> de la page « Événements » n'est visible que pour
            les comptes autorisés à créer des événements. S'il n'apparaît pas, contactez un administrateur.
          </li>
          <li>Le bouton <strong>« Types d'événement »</strong> <Num>2</Num> ouvre un court guide des types.</li>
          <li>
            Le type <strong>« Tournoi ouvert »</strong> n'est disponible que pour les comptes disposant d'un accès spécial.
            Les autres ne le voient pas dans la liste des types.
          </li>
          <li>Pour que les courses soient trouvées par modèle de recherche, l'auteur de l'événement ou un coorganisateur doit avoir connecté ROUVY.</li>
        </ul>
        <Shot
          name="events-list"
          alt="Page « Événements » : boutons « Types d'événement » et « Créer un événement »"
          caption="La page « Événements ». L'icône crayon sur la ligne de l'événement (3) ouvre la modification ; l'auteur de l'événement et les administrateurs la voient."
        />
      </section>

      <section id="choose">
        <h2>Choisir la variante de l'événement</h2>
        <p>La variante découle de deux choix : le <strong>type</strong> et <strong>la façon d'ajouter les courses</strong>.</p>

        <h3>Types d'événement</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Parcours</th>
                <th>Horaires</th>
                <th>Limite de courses par étape</th>
                <th>Wild cards</th>
                <th>Pour quoi</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Sortie en groupe</strong></td>
                <td>1</td>
                <td>Oui</td>
                <td>—</td>
                <td>—</td>
                <td>Une sortie conviviale ou un entraînement entre amis</td>
              </tr>
              <tr>
                <td><strong>Course</strong></td>
                <td>1</td>
                <td>Oui</td>
                <td>Oui</td>
                <td>Oui</td>
                <td>Une seule course, éventuellement avec plusieurs départs</td>
              </tr>
              <tr>
                <td><strong>Tournoi</strong></td>
                <td>1 ou plus</td>
                <td>Oui</td>
                <td>Oui</td>
                <td>Oui</td>
                <td>Une série d'étapes sur différents parcours</td>
              </tr>
              <tr>
                <td><strong>Tournoi ouvert</strong></td>
                <td>1 ou plus</td>
                <td>Non, seulement une période</td>
                <td>—</td>
                <td>—</td>
                <td>Chaque participant roule les parcours quand il veut ; le meilleur temps compte</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">« Parcours » : combien de parcours ROUVY différents l'événement peut avoir. Un parcours peut avoir plusieurs départs.</p>

        <h3>Façons d'ajouter les courses</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Façon</th>
                <th>Qui crée les courses sur ROUVY</th>
                <th>Types d'événement</th>
                <th>Comment l'activer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Modèle de recherche</strong></a></td>
                <td>L'auteur de l'événement ou les coorganisateurs</td>
                <td>Sortie en groupe, course, tournoi</td>
                <td>Par défaut</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. Manuellement</strong></a></td>
                <td>N'importe quel utilisateur ROUVY</td>
                <td>Sortie en groupe, course, tournoi</td>
                <td>Case « Courses créées par d'autres utilisateurs ROUVY »</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Parcours sans horaires</strong></a></td>
                <td>Chaque participant pour lui-même</td>
                <td>Tournoi ouvert</td>
                <td>Choisir le type « Tournoi ouvert »</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Événement officiel</strong></a></td>
                <td>ROUVY</td>
                <td>Tous sauf le tournoi ouvert</td>
                <td>Statut « Officiel »</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          La façon d'ajouter les courses se choisit à la création et ne peut plus être modifiée ensuite. En cas d'erreur,
          recréez l'événement.
        </Note>
      </section>

      <section id="basics">
        <h2>Étape 1. L'essentiel : nom, description, coorganisateurs</h2>
        <Shot name="create-basics" alt="Haut du formulaire de création d'événement" caption="Haut du formulaire pour la variante avec modèle de recherche" />
        <ol>
          <li><Num>1</Num> <strong>Nom de l'événement</strong> : obligatoire. Sans modèle de recherche, les courses ROUVY sont cherchées avec ce nom.</li>
          <li>
            <Num>2</Num> <strong>Courses créées par d'autres utilisateurs ROUVY</strong> : à cocher uniquement pour la <a href="#manual">variante B</a>.
            Le champ du modèle disparaît alors et une liste de courses apparaît.
          </li>
          <li>
            <Num>3</Num> <strong>Modèle de recherche Rouvy</strong> : la partie du nom commune à toutes vos courses ROUVY de cet événement,
            par exemple <code>Autumn Cup</code>. Voir la <a href="#sync">variante A</a>.
          </li>
          <li><Num>4</Num> <strong>Coorganisateurs</strong> : utilisateurs qui peuvent aussi ajouter des courses. Leurs courses ROUVY sont également trouvées par le modèle.</li>
          <li><Num>5</Num> <strong>Description</strong> : règles, format, liens. Les participants la voient sur la page de l'événement.</li>
        </ol>

        <h3>Ajouter un coorganisateur</h3>
        <p>Commencez à saisir un nom ou un pseudo (2 caractères minimum) et choisissez la personne dans la liste. Les personnes choisies apparaissent sous forme d'étiquettes ; la croix les retire.</p>
        <Shot name="co-organizers" alt="Recherche de coorganisateurs" caption="Un coorganisateur est déjà ajouté, un second est choisi dans la liste" />
      </section>

      <section id="type">
        <h2>Étape 2. Type d'événement</h2>
        <p>Sous la liste des types, le formulaire affiche une aide sur le type choisi. Le type détermine les champs qui suivent.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Type Sortie en groupe" caption="Sortie en groupe : un parcours, sans limite de courses ni wild cards" />
          <Shot name="type-race" alt="Type Course" caption="Course : un parcours, ajoute les courses par étape et les wild cards" />
          <Shot name="type-tournament" alt="Type Tournoi" caption="Tournoi : plusieurs parcours, mêmes champs supplémentaires que la course" />
          <Shot name="type-open_tournament" alt="Type Tournoi ouvert" caption="Tournoi ouvert : ajoute la période du tournoi" />
        </div>
      </section>

      <section id="status">
        <h2>Étape 3. Statut et image</h2>
        <Shot name="status-image" alt="Statut et image de l'événement" />
        <ol>
          <li>
            <Num>1</Num> <strong>Statut</strong> : « Personnel » convient presque toujours. « Officiel » est réservé aux événements officiels ROUVY,
            voir la <a href="#official">variante D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Image</strong> : la couverture de l'événement. Utilisez « Envoyer une image », jusqu'à 5 Mo.
            « Retirer l'image » supprime l'image choisie.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Étape 4. Inscriptions et visibilité</h2>
        <Shot name="registration" alt="Réglages des inscriptions" caption="Un tournoi : « Courses par étape » n'existe que pour les courses et les tournois" />
        <ol>
          <li>
            <Num>1</Num> <strong>Clôture des inscriptions (heures avant le départ)</strong> : combien d'heures avant la première course
            les inscriptions ferment. Vide : on peut s'inscrire même après le départ.
          </li>
          <li>
            <Num>2</Num> <strong>Courses par étape</strong> (course et tournoi) : combien de courses d'une étape un participant peut rouler pour le classement.
            Le participant s'inscrit à des courses précises, et seules celles-ci comptent. Vide : toutes les courses comptent.
          </li>
          <li><Num>3</Num> <strong>Événement long</strong> : les inscriptions restent ouvertes en permanence, même après toutes les courses.</li>
          <li>
            <Num>4</Num> <strong>Événement masqué</strong> : dans la liste des événements, seuls les administrateurs et les organisateurs le voient.
            Les résultats sont calculés normalement et le lien direct fonctionne pour tout le monde.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Étape 5. Classements et points</h2>
        <Shot name="scoring" alt="Classements et points" caption="La compétition par équipes est activée : les champs d'équipe sont apparus" />
        <ol>
          <li><Num>1</Num> <strong>Compétition individuelle</strong> et/ou <strong>Compétition par équipes</strong>. Les deux peuvent être activées.</li>
          <li><Num>2</Num> <strong>Points pour la 1re place</strong> : ce que reçoit le vainqueur de l'étape (1000 par défaut).</li>
          <li><Num>3</Num> <strong>Pas des points</strong> : combien de points en moins reçoit chaque place suivante (10 par défaut).</li>
          <li><Num>4</Num> <strong>Membres de l'équipe pris en compte</strong> (par équipes uniquement) : combien de meilleurs résultats de l'équipe comptent.</li>
          <li>
            <Num>5</Num> <strong>Nombre maximal de membres par équipe</strong> (par équipes uniquement) : combien de coureurs d'une équipe peuvent s'inscrire.
            Vide : pas de limite.
          </li>
          <li><Num>6</Num> <strong>Créer</strong> : enregistre l'événement et revient à la liste des événements.</li>
        </ol>
        <Note>
          Points pour une place : <code>points pour la 1re place − (place − 1) × pas</code>, sans descendre sous 0.
          Avec 1000 et 10 : 1re — 1000, 2e — 990, 10e — 910, à partir de la 101e — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Étape 6. Wild cards (course et tournoi)</h2>
        <p>Une wild card permet de rouler une étape en dehors des horaires. Ce bloc n'existe que pour les événements personnels de type Course et Tournoi.</p>
        <ol>
          <li><strong>Wild cards (nombre maximal)</strong> : combien de wild cards un participant peut utiliser dans l'événement. Vide : pas de wild cards.</li>
          <li>
            <strong>Qui crée la wild card</strong> (apparaît au-delà de 0 wild card) :
            <ul>
              <li><strong>Créée par l'organisateur</strong> : le participant indique une date et une heure, l'organisateur crée une course ROUVY pour lui et approuve la demande.</li>
              <li><strong>Créée par le participant</strong> : le participant crée une course ROUVY selon vos conditions et envoie le lien ; l'organisateur l'approuve ou la refuse.</li>
            </ul>
          </li>
          <li>
            <strong>Champ des conditions</strong> (uniquement « Créée par le participant ») : ce que la course du participant doit respecter : parcours, réglages, horaire.
            Les participants voient ce texte lorsqu'ils envoient une demande.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wild cards créées par l'organisateur" caption="Créée par l'organisateur" />
          <Shot name="wildcards-participant" alt="Wild cards créées par le participant" caption="Créée par le participant : le champ des conditions apparaît" />
        </div>
        <p>L'organisateur reçoit une notification pour chaque demande.</p>
      </section>

      <section id="sync">
        <h2>Variante A. Courses trouvées par modèle de recherche</h2>
        <p>C'est la variante par défaut. Vous (ou les coorganisateurs) créez les courses sur ROUVY, et le système les trouve grâce au modèle et les ajoute à l'événement.</p>
        <ol className="guide-steps">
          <li>Créez les courses sur ROUVY avec une partie commune dans le nom, par exemple <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Laissez la case « Courses créées par d'autres utilisateurs ROUVY » <strong>décochée</strong>.</li>
          <li>Saisissez la partie commune dans <strong>« Modèle de recherche Rouvy »</strong> : <code>Autumn Cup</code>. Vide : le nom de l'événement est utilisé.</li>
          <li>Ajoutez des coorganisateurs si d'autres personnes créent aussi des courses.</li>
          <li>Remplissez les autres étapes et cliquez sur <strong>« Créer »</strong>.</li>
          <li>La recherche des courses démarre en arrière-plan juste après la création. Les courses apparaissent en quelques minutes : vérifiez-les dans la modification de l'événement.</li>
        </ol>
        <p>La modification de l'événement affiche le bloc de synchronisation et la liste des courses trouvées :</p>
        <Shot name="edit-sync" alt="Synchronisation et courses ajoutées" />
        <ol>
          <li><Num>1</Num> <strong>Synchroniser les parcours</strong> : relancer la recherche, par exemple après avoir créé de nouvelles courses sur ROUVY. Vous pouvez fermer la page.</li>
          <li><Num>2</Num> <strong>Actualiser la liste</strong> : charger le résultat de la synchronisation sans recharger la page.</li>
          <li><Num>3</Num> <strong>Supprimer</strong> : retirer de l'événement une course en trop.</li>
        </ol>
        <Note>Si vous modifiez le nom de l'événement ou le modèle puis enregistrez, la recherche démarre automatiquement.</Note>
      </section>

      <section id="manual">
        <h2>Variante B. Courses d'autres utilisateurs ROUVY</h2>
        <p>Si ni vous ni les coorganisateurs n'avez créé les courses ROUVY, la recherche par modèle ne les trouvera pas. Les courses s'ajoutent alors manuellement.</p>
        <ol className="guide-steps">
          <li>Cochez <strong>« Courses créées par d'autres utilisateurs ROUVY »</strong>. Le champ du modèle disparaît et le bloc « Courses de l'événement » apparaît.</li>
          <li>
            Pour chaque course, renseignez :
            <ul>
              <li><strong>Parcours ROUVY*</strong> : identifiant du parcours ou lien du type <code>https://riders.rouvy.com/route/12345</code> ;</li>
              <li><strong>Nom du parcours</strong> : facultatif, si vous voulez le vôtre ;</li>
              <li><strong>Lien de la course ROUVY*</strong> : <code>https://riders.rouvy.com/events/…</code> ;</li>
              <li><strong>Nom de la course</strong> : facultatif ; vide, il est repris de ROUVY ;</li>
              <li><strong>Date et heure de départ*</strong> : dans votre fuseau horaire.</li>
            </ul>
          </li>
          <li><strong>« Ajouter une course »</strong> ajoute une ligne, <strong>« Retirer »</strong> la supprime. Les lignes vides ne sont pas enregistrées.</li>
          <li>Cliquez sur <strong>« Créer »</strong>. Si une course échoue, l'événement n'est pas créé et l'erreur indique le numéro de ligne.</li>
        </ol>
        <Shot name="manual-create" alt="Courses de l'événement à la création" caption="La liste des courses à la création de l'événement" />
        <p>Vous pouvez aussi ajouter des courses plus tard : la modification de l'événement propose un formulaire pour une course <Num>1</Num>. La course ajoutée apparaît aussitôt dans la liste <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Ajouter une course dans la modification" />
      </section>

      <section id="open">
        <h2>Variante C. Tournoi ouvert</h2>
        <p>
          Un tournoi ouvert n'a pas d'horaires. Chaque participant crée sur ROUVY une course (Race) sur un parcours du tournoi et la roule quand il veut.
          La sortie compte selon le parcours et le type de course, et le meilleur temps de toutes les tentatives entre au classement.
        </p>
        <ol className="guide-steps">
          <li>Dans « Type d'événement », choisissez <strong>« Tournoi ouvert »</strong>.</li>
          <li>
            Définissez la <strong>période du tournoi</strong> : « Début du tournoi » et « Fin du tournoi ». Les sorties en dehors ne comptent pas.
            Les deux sont facultatives : sans bornes, le tournoi n'a pas de fin. La fin doit être postérieure au début.
          </li>
          <li>Remplissez les autres étapes et cliquez sur <strong>« Créer »</strong>.</li>
          <li>Ouvrez la modification de l'événement et ajoutez les parcours (voir ci-dessous).</li>
        </ol>
        <Note kind="warning">Les parcours d'un tournoi ouvert ne s'ajoutent qu'après la création de l'événement, dans sa modification.</Note>
        <Shot name="edit-open" alt="Parcours du tournoi ouvert" />
        <ol>
          <li><Num>1</Num> Collez le lien de la page d'un parcours ROUVY : <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Cliquez sur <strong>« Ajouter un parcours »</strong>.</li>
          <li><Num>3</Num> Le parcours rejoint le tournoi. « Supprimer » le retire.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Variante D. Événement officiel</h2>
        <p>
          Le statut <strong>« Officiel »</strong> est réservé aux événements officiels ROUVY. Leurs courses sont gérées par ROUVY, le formulaire est donc plus court :
          pas de case pour les courses manuelles, de modèle de recherche, de coorganisateurs ni de wild cards.
        </p>
        <Shot name="official" alt="Formulaire d'un événement officiel" caption="Statut « Officiel » : les blocs superflus sont masqués" />
      </section>

      <section id="after">
        <h2>Après la création : modification et coorganisateurs</h2>
        <ul>
          <li>La modification s'ouvre avec l'icône crayon sur la ligne de l'événement, page « Événements ». Tous les réglages sont modifiables par l'auteur de l'événement et les administrateurs.</li>
          <li>La façon d'ajouter les courses (la case « Courses créées par d'autres utilisateurs ROUVY ») ne peut pas être modifiée.</li>
          <li>Les coorganisateurs voient une page simplifiée : ils peuvent ajouter des courses (lancer la synchronisation ou les saisir manuellement) et supprimer celles qu'ils ont ajoutées. Les autres réglages ne leur sont pas accessibles.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Page du coorganisateur" caption="Ce que voit un coorganisateur : il ne peut supprimer que sa propre course" />
      </section>

      <section id="problems">
        <h2>Erreurs fréquentes</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Message ou situation</th>
                <th>Cause et solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>« Le nom de l'événement est obligatoire »</td>
                <td>Remplissez « Nom de l'événement ».</td>
              </tr>
              <tr>
                <td>« Les sorties en groupe et les courses ne peuvent avoir qu'un seul parcours »</td>
                <td>L'événement a des courses sur différents parcours. Choisissez le type Tournoi ou retirez les courses en trop.</td>
              </tr>
              <tr>
                <td>« Le tournoi doit se terminer après avoir commencé »</td>
                <td>Corrigez la période du tournoi ouvert.</td>
              </tr>
              <tr>
                <td>« Indiquez le parcours, le lien de la course ROUVY et la date/heure de départ pour chaque course »</td>
                <td>Il manque un champ obligatoire dans une ligne des courses manuelles. Remplissez-le ou retirez la ligne.</td>
              </tr>
              <tr>
                <td>Le tournoi ouvert est refusé à l'enregistrement</td>
                <td>Le compte n'a pas accès aux tournois ouverts. Choisissez un autre type.</td>
              </tr>
              <tr>
                <td>Aucune course après la synchronisation</td>
                <td>
                  Vérifiez que les noms des courses ROUVY contiennent le modèle, qu'elles ont été créées par vous ou un coorganisateur
                  et que votre ROUVY est connecté. Patientez quelques minutes puis cliquez sur « Actualiser la liste ».
                  Si quelqu'un d'autre a créé les courses, il faut la <a href="#manual">variante B</a> : recréez l'événement.
                </td>
              </tr>
              <tr>
                <td>Pas de bouton « Créer un événement »</td>
                <td>Le compte ne peut pas créer d'événements : contactez un administrateur.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
